import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MapPin, Users, Clock, Shield, TrendingUp, Smartphone } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,255,102,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(0,255,102,0.15),transparent_50%)]" />
        
        <nav className="relative px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif', color: '#00FF66'}}>TurfSense</span>
          </div>
          <Button 
            onClick={() => navigate('/auth')} 
            className="bg-primary hover:bg-accent text-black px-6 font-bold"
            data-testid="nav-login-btn"
            style={{backgroundColor: '#00FF66'}}
          >
            Login
          </Button>
        </nav>

        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="text-center space-y-8">
            <h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight"
              style={{fontFamily: 'Space Grotesk, sans-serif', color: '#00FF66'}}
              data-testid="hero-title"
            >
              Smart Sports Ground
              <br />Management Platform
            </h1>
            <p className="text-lg max-w-2xl mx-auto" style={{fontFamily: 'Inter, sans-serif', color: '#FFFFFF'}}>
              AI-powered attendance tracking with face recognition, real-time geofencing, and automated payroll for seamless turf operations.
            </p>
            <div className="flex gap-4 justify-center">
              <Button 
                onClick={() => navigate('/auth')} 
                size="lg" 
                className="px-8 h-12 text-base font-bold text-black"
                style={{backgroundColor: '#00FF66'}}
                data-testid="hero-get-started-btn"
              >
                Get Started
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="px-8 h-12 text-base"
                style={{borderColor: '#00FF66', color: '#00FF66'}}
                data-testid="learn-more-btn"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 px-6 max-w-7xl mx-auto">
        <h2 
          className="text-4xl font-bold text-center mb-16 text-gray-800"
          style={{fontFamily: 'Space Grotesk, sans-serif'}}
        >
          Powerful Features for Modern Turf Management
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <FeatureCard 
            icon={<Shield className="h-8 w-8 text-emerald-600" />}
            title="AI Face Recognition"
            description="Secure attendance with live face detection and matching technology"
          />
          <FeatureCard 
            icon={<MapPin className="h-8 w-8 text-teal-600" />}
            title="Smart Geofencing"
            description="Location-based check-ins with 50km radius validation"
          />
          <FeatureCard 
            icon={<Users className="h-8 w-8 text-cyan-600" />}
            title="Multi-Role Access"
            description="Separate portals for Admin, Employees, and Clients"
          />
          <FeatureCard 
            icon={<Clock className="h-8 w-8 text-emerald-600" />}
            title="Real-Time Sync"
            description="Live data synchronization with offline mode support"
          />
          <FeatureCard 
            icon={<TrendingUp className="h-8 w-8 text-teal-600" />}
            title="Auto Payroll"
            description="Automated salary calculation based on attendance records"
          />
          <FeatureCard 
            icon={<Smartphone className="h-8 w-8 text-cyan-600" />}
            title="Mobile Optimized"
            description="Works seamlessly on mobile browsers and desktops"
          />
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-xl font-bold mb-4 block" style={{fontFamily: 'Space Grotesk, sans-serif'}}>TurfSense</span>
          <p className="text-gray-400 mb-4">Smart Sports Ground Management Platform</p>
          <p className="text-sm text-gray-500">&copy; 2025 TurfSense. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-shadow border border-gray-100">
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 w-16 h-16 rounded-xl flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2 text-gray-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>{title}</h3>
      <p className="text-gray-600" style={{fontFamily: 'Inter, sans-serif'}}>{description}</p>
    </div>
  );
}